import React, { useState, useRef, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { 
  ImageIcon, 
  Trash2, 
  MapPin, 
  Camera, 
  Utensils, 
  FileText, 
  Coffee, 
  User, 
  Clock, 
  Save, 
  Lock, 
  Building2, 
  Tag as TagIcon,
  Check,
  Search
} from 'lucide-react';
import { toast } from 'react-toastify';
import axios from 'axios';

const RestaurantProfile = () => {
  const context = useOutletContext();
  const { 
    userData = {}, 
    setUserData, 
    setGalleryConfig, 
    formDataRef,
    logoInputRef,
    fileInputRef,
    ambienceFileInputRef,
    foodFileInputRef,
    menuFileInputRef,
    beverageFileInputRef,
    handleLogoUpload,
    handleImageUpload,
    handleAmbienceImageUpload,
    handleFoodImageUpload,
    handleMenuImageUpload,
    handleBeverageImageUpload,
    removeImage,
    removeAmbienceImage,
    removeFoodImage,
    removeMenuImage,
    removeBeverageImage
  } = context || {};

  const localFormDataRef = useRef(formDataRef?.current || userData || {});
  
  // Keep local ref in sync with latest formDataRef or userData
  useEffect(() => {
    if (formDataRef?.current && Object.keys(formDataRef.current).length > 0) {
      localFormDataRef.current = { ...formDataRef.current };
    } else if (userData && Object.keys(userData).length > 0) {
      localFormDataRef.current = { ...userData };
      if (formDataRef) formDataRef.current = { ...userData };
    }
  }, [userData, formDataRef]);

  const [activeTab, setActiveTab] = useState('basic');
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [hasSecondShift, setHasSecondShift] = useState(
    Boolean(userData?.businessHours?.openingTime2 || userData?.businessHours?.closingTime2)
  );

  const [mapUrlInput, setMapUrlInput] = useState(userData?.map_url || '');
  const [latitudeInput, setLatitudeInput] = useState(userData?.latitude || '');
  const [longitudeInput, setLongitudeInput] = useState(userData?.longitude || '');
  
  const [baselineCommission, setBaselineCommission] = useState(Number(userData?.commission || 0));
  const [commissionValue, setCommissionValue] = useState(userData?.commission ?? 0);

  const [businessHoursState, setBusinessHoursState] = useState({
    openingTime: userData?.businessHours?.openingTime || '',
    closingTime: userData?.businessHours?.closingTime || '',
    openingTime2: userData?.businessHours?.openingTime2 || '',
    closingTime2: userData?.businessHours?.closingTime2 || '',
    days: userData?.businessHours?.days || []
  });

  const [tagsList, setTagsList] = useState([]);
  const [isTagsLoading, setIsTagsLoading] = useState(false);
  const [selectedPrimaryTags, setSelectedPrimaryTags] = useState([]);
  const [selectedSecondaryTags, setSelectedSecondaryTags] = useState([]);
  const [tagSearchQuery, setTagSearchQuery] = useState('');
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BASE_URL}/location/dropdown/list`)
      .then((response) => response.json())
      .then((data) => {
        setLocations(data.data || []);
      })
      .catch((error) => console.error("Error fetching locations:", error));
  }, []);

  // Fetch Tags from Backend API (/api/tags?type=restaurant)
  useEffect(() => {
    const fetchRestaurantTags = async () => {
      setIsTagsLoading(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/tags?type=restaurant`);
        const allTags = response.data || [];
        // Strictly filter for non-primary restaurant tags only (partners only manage secondary tags)
        const restaurantOnlyTags = allTags.filter(t => {
          if (t.isPrimary) return false;
          if (!t.type) return false;
          if (Array.isArray(t.type)) return t.type.includes('restaurant');
          return t.type === 'restaurant';
        }).filter(t => t.isActive !== false);

        setTagsList(restaurantOnlyTags);
      } catch (error) {
        console.error('Error fetching restaurant tags:', error);
      } finally {
        setIsTagsLoading(false);
      }
    };

    fetchRestaurantTags();
  }, []);

  // Sync selected tags from userData or formDataRef
  useEffect(() => {
    const data = formDataRef?.current && Object.keys(formDataRef.current).length > 0
      ? formDataRef.current
      : (userData || {});

    if (data.primary_tags && Array.isArray(data.primary_tags)) {
      const ids = data.primary_tags.map(t => typeof t === 'object' && t ? (t._id || t.id) : t).filter(Boolean);
      setSelectedPrimaryTags(ids);
    }

    if (data.secondary_tags && Array.isArray(data.secondary_tags)) {
      const ids = data.secondary_tags.map(t => typeof t === 'object' && t ? (t._id || t.id) : t).filter(Boolean);
      setSelectedSecondaryTags(ids);
    }
  }, [userData, formDataRef]);

  const togglePrimaryTag = (tagId) => {
    setSelectedPrimaryTags(prev => {
      const exists = prev.includes(tagId);
      const updated = exists ? prev.filter(id => id !== tagId) : [...prev, tagId];
      
      const refToUpdate = formDataRef?.current ? formDataRef.current : localFormDataRef.current;
      refToUpdate.primary_tags = updated;
      if (formDataRef) formDataRef.current.primary_tags = updated;

      return updated;
    });
  };

  const toggleSecondaryTag = (tagId) => {
    setSelectedSecondaryTags(prev => {
      const exists = prev.includes(tagId);
      const updated = exists ? prev.filter(id => id !== tagId) : [...prev, tagId];
      
      const refToUpdate = formDataRef?.current ? formDataRef.current : localFormDataRef.current;
      refToUpdate.secondary_tags = updated;
      if (formDataRef) formDataRef.current.secondary_tags = updated;

      return updated;
    });
  };

  useEffect(() => {
    const data = formDataRef?.current && Object.keys(formDataRef.current).length > 0
      ? formDataRef.current
      : (userData || {});

    if (data.map_url !== undefined) setMapUrlInput(data.map_url || '');
    if (data.latitude !== undefined) setLatitudeInput(data.latitude || '');
    if (data.longitude !== undefined) setLongitudeInput(data.longitude || '');
    if (data.commission !== undefined && data.commission !== null) {
      const comm = Number(data.commission || 0);
      setBaselineCommission(comm);
      setCommissionValue(prev => (prev === 0 || prev === '' ? comm : prev));
    }
    if (data.businessHours) {
      setBusinessHoursState({
        openingTime: data.businessHours.openingTime || '',
        closingTime: data.businessHours.closingTime || '',
        openingTime2: data.businessHours.openingTime2 || '',
        closingTime2: data.businessHours.closingTime2 || '',
        days: data.businessHours.days || []
      });
      if (data.businessHours.openingTime2 || data.businessHours.closingTime2) {
        setHasSecondShift(true);
      }
    }
  }, [userData, formDataRef]);

  const handleCommissionValidate = (val) => {
    const minAllowed = Number(baselineCommission ?? 0);
    const numVal = Number(val);

    if (isNaN(numVal) || numVal < minAllowed) {
      toast.error(`Commission cannot be decreased below your existing rate (${minAllowed}%). To decrease your rate, please contact Trrip Support.`);
      setCommissionValue(minAllowed);
      const refToUpdate = formDataRef?.current ? formDataRef.current : localFormDataRef.current;
      refToUpdate.commission = minAllowed;
      if (formDataRef) formDataRef.current.commission = minAllowed;
      return minAllowed;
    } else {
      setCommissionValue(numVal);
      const refToUpdate = formDataRef?.current ? formDataRef.current : localFormDataRef.current;
      refToUpdate.commission = numVal;
      if (formDataRef) formDataRef.current.commission = numVal;
      return numVal;
    }
  };

  const handleExtractCoordinates = (urlToExtract) => {
    const url = urlToExtract || mapUrlInput || '';
    if (!url || !url.trim()) {
      toast.error('Please enter or paste a Google Maps URL first.');
      return;
    }

    let lat = '';
    let lng = '';

    // Regex 1: @lat,lng format (e.g. google.com/maps/@15.4989,73.8278,17z)
    const atMatch = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
    if (atMatch) {
      lat = atMatch[1];
      lng = atMatch[2];
    }

    // Regex 2: !3dLat!4dLong format (e.g. google.com/maps/...!3d15.4989!4d73.8278)
    if (!lat || !lng) {
      const dMatch = url.match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/);
      if (dMatch) {
        lat = dMatch[1];
        lng = dMatch[2];
      }
    }

    // Regex 3: q=lat,lng or ll=lat,lng format
    if (!lat || !lng) {
      const queryMatch = url.match(/[?&](?:q|ll)=(?:loc:)?(-?\d+\.\d+),(-?\d+\.\d+)/);
      if (queryMatch) {
        lat = queryMatch[1];
        lng = queryMatch[2];
      }
    }

    // Regex 4: Generic coordinate pair pattern
    if (!lat || !lng) {
      const genericMatch = url.match(/(-?\d{1,2}\.\d+)[,\s]+(-?\d{1,3}\.\d+)/);
      if (genericMatch) {
        lat = genericMatch[1];
        lng = genericMatch[2];
      }
    }

    if (lat && lng) {
      setLatitudeInput(lat);
      setLongitudeInput(lng);

      const refToUpdate = formDataRef?.current ? formDataRef.current : localFormDataRef.current;
      refToUpdate.latitude = parseFloat(lat);
      refToUpdate.longitude = parseFloat(lng);
      refToUpdate.map_url = url;

      if (formDataRef) {
        formDataRef.current.latitude = parseFloat(lat);
        formDataRef.current.longitude = parseFloat(lng);
        formDataRef.current.map_url = url;
      }

      if (setUserData) {
        setUserData(prev => ({
          ...prev,
          latitude: parseFloat(lat),
          longitude: parseFloat(lng),
          map_url: url
        }));
      }

      toast.success(`Extracted! Latitude: ${lat}, Longitude: ${lng}`);
    } else {
      toast.error('Could not extract coordinates. Please check the URL or type coordinates manually.');
    }
  };

  const currentPasswordRef = useRef('');
  const newPasswordRef = useRef('');

  const daysOfWeek = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday'
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const refToUpdate = formDataRef?.current ? formDataRef.current : localFormDataRef.current;

    if (name.startsWith('businessHours.')) {
      const field = name.split('.')[1];
      const updatedHours = {
        ...(refToUpdate.businessHours || {}),
        [field]: value
      };
      refToUpdate.businessHours = updatedHours;
      if (formDataRef) formDataRef.current.businessHours = updatedHours;
      setBusinessHoursState(updatedHours);
    } else {
      const updatedValue = type === 'checkbox' ? checked : value;
      refToUpdate[name] = updatedValue;
      if (formDataRef) formDataRef.current[name] = updatedValue;

      if (name === 'owner_name') {
        refToUpdate.name = value;
        if (formDataRef) formDataRef.current.name = value;
      }
    }
  };

  const handleDaysChange = (event) => {
    const { value, checked } = event.target;
    const refToUpdate = formDataRef?.current ? formDataRef.current : localFormDataRef.current;
    const currentDays = refToUpdate.businessHours?.days || [];

    const updatedDays = checked
      ? [...currentDays, value]
      : currentDays.filter((day) => day !== value);

    const updatedHours = {
      ...(refToUpdate.businessHours || {}),
      days: updatedDays
    };

    refToUpdate.businessHours = updatedHours;
    if (formDataRef) formDataRef.current.businessHours = updatedHours;
    setBusinessHoursState(updatedHours);

    if (setUserData) {
      setUserData(prev => ({
        ...prev,
        businessHours: {
          ...(prev?.businessHours || {}),
          days: updatedDays
        }
      }));
    }
  };

  const handleSaveChanges = async () => {
    setIsLoading(true);
    let token = null;
    let id = null;
    const categories = ['restaurant', 'shop', 'activities'];

    for (const category of categories) {
      const storedToken = localStorage.getItem(`token_partner_${category}`);
      const storedId = localStorage.getItem(`id_partner_${category}`);
      if (storedToken && storedId) {
        token = storedToken;
        id = storedId;
        break;
      }
    }

    const currentData = formDataRef?.current ? formDataRef.current : localFormDataRef.current;

    if (!currentData.description || !currentData.description.trim()) {
      toast.error('Restaurant description is required.');
      setIsLoading(false);
      return;
    }

    const minAllowedCommission = Number(baselineCommission ?? 0);
    const submittedCommission = Number(currentData.commission ?? 0);

    if (submittedCommission < minAllowedCommission) {
      toast.error(`Commission cannot be decreased below your existing rate (${minAllowedCommission}%). Please contact Trrip Support.`);
      setCommissionValue(minAllowedCommission);
      const refToUpdate = formDataRef?.current ? formDataRef.current : localFormDataRef.current;
      refToUpdate.commission = minAllowedCommission;
      if (formDataRef) formDataRef.current.commission = minAllowedCommission;
      setIsLoading(false);
      return;
    }

    const updateData = { ...currentData };
    if (Array.isArray(updateData.logo_url)) {
      updateData.logo_url = updateData.logo_url[0] || '';
    }

    if (showPasswordFields) {
      if (currentPasswordRef.current?.value && newPasswordRef.current?.value) {
        updateData.currentPassword = currentPasswordRef.current.value;
        updateData.newPassword = newPasswordRef.current.value;
      }
    }

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/auth/${id}/`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.status === 200) {
        toast.success('Restaurant profile updated successfully!');
        const savedComm = Number(updateData.commission || 0);
        setBaselineCommission(savedComm);
        setCommissionValue(savedComm);
        if (setUserData) setUserData(updateData);
        if (formDataRef) formDataRef.current = updateData;
        setShowPasswordFields(false);
        if (currentPasswordRef.current) currentPasswordRef.current.value = '';
        if (newPasswordRef.current) newPasswordRef.current.value = '';
      }
    } catch (error) {
      console.error('Error updating restaurant details:', error);
      toast.error(error.response?.data?.error || 'Failed to update restaurant profile.');
    } finally {
      setIsLoading(false);
    }
  };

  const currentData = formDataRef?.current && Object.keys(formDataRef.current).length > 0
    ? formDataRef.current 
    : (userData || {});

  const logoDisplayUrl = Array.isArray(currentData.logo_url) ? currentData.logo_url[0] : currentData.logo_url;

  return (
    <div className="space-y-6 max-w-6xl mx-auto pb-12 animate-page-entry">
      {/* Top Banner & Title Bar */}
      <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary">
            <Building2 size={24} />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-black text-gray-900 tracking-tight">Restaurant Profile</h1>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mt-0.5">Manage details, timings & photo gallery</p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleSaveChanges}
          disabled={isLoading}
          className="flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-lg shadow-primary/20 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
        >
          <Save size={16} />
          <span>{isLoading ? 'Saving...' : 'Save Changes'}</span>
        </button>
      </div>

      {/* TAB NAVIGATION BAR (Full Section Width) */}
      <div className="w-full p-1 mb-2">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 w-full">
          <button
            type="button"
            onClick={() => setActiveTab('basic')}
            className={`w-full flex items-center justify-center gap-2 px-3 sm:px-4 py-3 rounded-2xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer ${
              activeTab === 'basic'
                ? 'bg-primary text-white shadow-md shadow-primary/20 ring-2 ring-primary'
                : 'bg-white text-gray-500 hover:text-gray-900 hover:bg-gray-100/70 border border-gray-200/60 shadow-2xs'
            }`}
          >
            <User size={16} className="shrink-0" />
            <span className="truncate">Basic Info & Logo</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('images')}
            className={`w-full flex items-center justify-center gap-2 px-3 sm:px-4 py-3 rounded-2xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer ${
              activeTab === 'images'
                ? 'bg-primary text-white shadow-md shadow-primary/20 ring-2 ring-primary'
                : 'bg-white text-gray-500 hover:text-gray-900 hover:bg-gray-100/70 border border-gray-200/60 shadow-2xs'
            }`}
          >
            <Camera size={16} className="shrink-0" />
            <span className="truncate">Images & Gallery</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('location')}
            className={`w-full flex items-center justify-center gap-2 px-3 sm:px-4 py-3 rounded-2xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer ${
              activeTab === 'location'
                ? 'bg-primary text-white shadow-md shadow-primary/20 ring-2 ring-primary'
                : 'bg-white text-gray-500 hover:text-gray-900 hover:bg-gray-100/70 border border-gray-200/60 shadow-2xs'
            }`}
          >
            <MapPin size={16} className="shrink-0" />
            <span className="truncate">Address & Operating Hours</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('tags')}
            className={`w-full flex items-center justify-center gap-2 px-3 sm:px-4 py-3 rounded-2xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer ${
              activeTab === 'tags'
                ? 'bg-primary text-white shadow-md shadow-primary/20 ring-2 ring-primary'
                : 'bg-white text-gray-500 hover:text-gray-900 hover:bg-gray-100/70 border border-gray-200/60 shadow-2xs'
            }`}
          >
            <TagIcon size={16} className="shrink-0" />
            <span className="truncate">Restaurant Tags</span>
          </button>
        </div>
      </div>

      {/* TAB 1: BASIC INFO & LOGO */}
      {activeTab === 'basic' && (
        <div key="basic" className="bg-white p-6 sm:p-8 rounded-3xl border border-gray-100 shadow-sm space-y-8 animate-tab-switch">
          {/* RESTAURANT LOGO */}
          <div className="bg-slate-50/80 p-5 rounded-2xl border border-slate-200/60">
            <label className="block text-xs font-black uppercase tracking-wider text-gray-700 mb-3">Restaurant Logo</label>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
              {logoDisplayUrl ? (
                <div className="relative group">
                  <img
                    src={logoDisplayUrl}
                    alt="Restaurant Logo"
                    className="w-24 h-24 rounded-2xl object-cover cursor-pointer hover:opacity-90 transition-opacity border border-gray-200 shadow-sm"
                    onClick={() => setGalleryConfig && setGalleryConfig({ isOpen: true, images: [logoDisplayUrl], index: 0 })}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (formDataRef) formDataRef.current.logo_url = '';
                      if (setUserData) setUserData(prev => ({ ...prev, logo_url: '' }));
                    }}
                    className="absolute -top-2 -right-2 bg-rose-500 text-white rounded-full p-1.5 shadow-md hover:scale-110 transition-transform"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-2xl flex items-center justify-center bg-white">
                  <ImageIcon className="w-8 h-8 text-gray-400" />
                </div>
              )}
              <div>
                <input type="file" ref={logoInputRef} onChange={(e) => handleLogoUpload && handleLogoUpload(e.target.files[0])} accept="image/*" className="hidden" />
                <button 
                  type="button" 
                  onClick={() => logoInputRef?.current?.click()} 
                  className="px-5 py-2.5 bg-primary text-white rounded-xl hover:bg-primary/90 text-xs font-bold shadow-md shadow-primary/10 transition-all"
                >
                  {logoDisplayUrl ? 'Change Logo' : 'Upload Logo'}
                </button>
              </div>
            </div>
          </div>

          {/* BASIC DETAILS */}
          <div>
            <h3 className="text-base font-extrabold text-gray-800 mb-4 uppercase tracking-tight flex items-center gap-2">
              <User className="w-5 h-5 text-primary" /> Basic Information
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Business Name</label>
                <input 
                  type="text" 
                  name="business_name" 
                  defaultValue={currentData.business_name || ''} 
                  onChange={handleInputChange} 
                  className="w-full p-3 border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all" 
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Owner Name</label>
                <input 
                  type="text" 
                  name="owner_name" 
                  defaultValue={currentData.owner_name || currentData.name || ''} 
                  onChange={handleInputChange} 
                  className="w-full p-3 border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all" 
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Location</label>
                <select
                  key={`loc-${locations.length}-${currentData.location_id?._id || currentData.location_id || ''}`}
                  name="location_id"
                  defaultValue={currentData.location_id?._id || currentData.location_id || ''}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-gray-700"
                >
                  <option value="" disabled>Select a location</option>
                  {locations.map((loc) => (
                    <option key={loc._id} value={loc._id}>
                      {loc.name} {loc.type ? `(${loc.type})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Phone Number</label>
                <input 
                  type="tel" 
                  name="phone_number" 
                  defaultValue={currentData.phone_number || ''} 
                  onChange={handleInputChange} 
                  className="w-full p-3 border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all" 
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Commission (%)</label>
                <input 
                  type="number" 
                  name="commission" 
                  min={baselineCommission ?? 0}
                  max="100"
                  step="0.1"
                  value={commissionValue} 
                  onChange={(e) => {
                    setCommissionValue(e.target.value);
                    const refToUpdate = formDataRef?.current ? formDataRef.current : localFormDataRef.current;
                    refToUpdate.commission = e.target.value ? Number(e.target.value) : 0;
                    if (formDataRef) formDataRef.current.commission = e.target.value ? Number(e.target.value) : 0;
                  }}
                  onBlur={(e) => handleCommissionValidate(e.target.value)}
                  placeholder="e.g. 10"
                  className="w-full p-3 border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all" 
                />
                <p className="text-[11px] text-gray-500 font-medium mt-1">
                  Current rate: <span className="font-bold text-gray-700">{baselineCommission}%</span>. You can increase this rate anytime. To decrease, please contact Trrip support.
                </p>
              </div>
            </div>
          </div>

          {/* RESTAURANT DESCRIPTION */}
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">
              Restaurant Description <span className="text-rose-500">*</span>
            </label>
            <textarea
              name="description"
              defaultValue={currentData.description || ''}
              onChange={handleInputChange}
              rows={4}
              required
              placeholder="Write a brief overview/description of your restaurant..."
              className="w-full p-3 border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
            />
          </div>

          {/* PASSWORD SECURITY */}
          <div className="border-t pt-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-extrabold text-gray-800 uppercase tracking-tight flex items-center gap-2">
                <Lock className="w-5 h-5 text-primary" /> Security & Password
              </h3>
              <button
                type="button"
                onClick={() => setShowPasswordFields(prev => !prev)}
                className="text-xs font-bold text-primary hover:underline"
              >
                {showPasswordFields ? 'Cancel Password Change' : 'Change Password'}
              </button>
            </div>

            {showPasswordFields && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-slate-50/80 p-5 rounded-2xl border border-slate-200/60 animate-in fade-in duration-200">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Current Password</label>
                  <input
                    type="password"
                    ref={currentPasswordRef}
                    placeholder="Enter current password"
                    className="w-full p-3 border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">New Password</label>
                  <input
                    type="password"
                    ref={newPasswordRef}
                    placeholder="Enter new password"
                    className="w-full p-3 border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  />
                </div>
              </div>
            )}
          </div>

          {/* SAVE BUTTON AT BOTTOM */}
          <div className="border-t pt-6 flex justify-end">
            <button
              type="button"
              onClick={handleSaveChanges}
              disabled={isLoading}
              className="flex items-center gap-2 px-8 py-3.5 bg-primary hover:bg-primary/90 text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-lg shadow-primary/20 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
            >
              <Save size={18} />
              <span>{isLoading ? 'Saving Changes...' : 'Save Basic Info'}</span>
            </button>
          </div>
        </div>
      )}

      {/* TAB 2: IMAGES & GALLERY */}
      {activeTab === 'images' && (
        <div key="images" className="bg-white p-6 sm:p-8 rounded-3xl border border-gray-100 shadow-sm space-y-8 animate-tab-switch">
          {/* RESTAURANT IMAGES (Ambience + Food Images) */}
          <div className="space-y-6">
            <h3 className="text-base font-extrabold text-primary flex items-center gap-2 uppercase tracking-tight">
              <Camera className="w-5 h-5 text-primary" /> Restaurant Photo Galleries
            </h3>

            {/* Ambience Images */}
            <div className="bg-slate-50/80 p-5 rounded-2xl border border-slate-200/60">
              <label className="block text-xs font-black text-gray-700 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Camera className="w-4 h-4 text-emerald-600" />
                Ambience Images ({currentData.ambience_image_url?.length || 0})
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {currentData.ambience_image_url?.map((image, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={image}
                      alt={`Ambience ${index+1}`}
                      className="w-full h-28 object-cover rounded-xl cursor-pointer border border-gray-200 shadow-sm"
                      onClick={() => setGalleryConfig && setGalleryConfig({ isOpen: true, images: currentData.ambience_image_url, index })}
                    />
                    <button
                      type="button"
                      onClick={() => removeAmbienceImage && removeAmbienceImage(index)}
                      className="absolute top-1.5 right-1.5 bg-rose-500 text-white rounded-full p-1.5 shadow-md hover:scale-110 transition-transform"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                <div 
                  className="border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:bg-white h-28 bg-white/50 transition-all group"
                  onClick={() => ambienceFileInputRef?.current?.click()}
                >
                  <ImageIcon className="w-6 h-6 text-gray-400 mb-1 group-hover:scale-110 transition-transform" />
                  <span className="text-xs font-bold text-gray-600">Add Ambience</span>
                  <input type="file" ref={ambienceFileInputRef} onChange={(e) => handleAmbienceImageUpload && handleAmbienceImageUpload(e.target.files)} accept="image/*" multiple className="hidden" />
                </div>
              </div>
            </div>

            {/* Food Images */}
            <div className="bg-slate-50/80 p-5 rounded-2xl border border-slate-200/60">
              <label className="block text-xs font-black text-gray-700 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Utensils className="w-4 h-4 text-amber-600" />
                Food Images ({currentData.food_image_url?.length || 0})
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {currentData.food_image_url?.map((image, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={image}
                      alt={`Food ${index+1}`}
                      className="w-full h-28 object-cover rounded-xl cursor-pointer border border-gray-200 shadow-sm"
                      onClick={() => setGalleryConfig && setGalleryConfig({ isOpen: true, images: currentData.food_image_url, index })}
                    />
                    <button
                      type="button"
                      onClick={() => removeFoodImage && removeFoodImage(index)}
                      className="absolute top-1.5 right-1.5 bg-rose-500 text-white rounded-full p-1.5 shadow-md hover:scale-110 transition-transform"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                <div 
                  className="border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:bg-white h-28 bg-white/50 transition-all group"
                  onClick={() => foodFileInputRef?.current?.click()}
                >
                  <ImageIcon className="w-6 h-6 text-gray-400 mb-1 group-hover:scale-110 transition-transform" />
                  <span className="text-xs font-bold text-gray-600">Add Food Images</span>
                  <input type="file" ref={foodFileInputRef} onChange={(e) => handleFoodImageUpload && handleFoodImageUpload(e.target.files)} accept="image/*" multiple className="hidden" />
                </div>
              </div>
            </div>

            {/* General Restaurant Photos (if any) */}
            {currentData.image_url?.length > 0 && (
              <div className="bg-slate-50/80 p-5 rounded-2xl border border-slate-200/60">
                <label className="block text-xs font-black text-gray-700 uppercase tracking-wider mb-3">
                  Other Restaurant Photos ({currentData.image_url?.length || 0})
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {currentData.image_url?.map((image, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={image}
                        alt={`General ${index+1}`}
                        className="w-full h-28 object-cover rounded-xl cursor-pointer border border-gray-200 shadow-sm"
                        onClick={() => setGalleryConfig && setGalleryConfig({ isOpen: true, images: currentData.image_url, index })}
                      />
                      <button
                        type="button"
                        onClick={() => removeImage && removeImage(index)}
                        className="absolute top-1.5 right-1.5 bg-rose-500 text-white rounded-full p-1.5 shadow-md hover:scale-110 transition-transform"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  <div 
                    className="border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:bg-white h-28 bg-white/50 transition-all group"
                    onClick={() => fileInputRef?.current?.click()}
                  >
                    <ImageIcon className="w-6 h-6 text-gray-400 mb-1 group-hover:scale-110 transition-transform" />
                    <span className="text-xs font-bold text-gray-600">Add Photos</span>
                    <input type="file" ref={fileInputRef} onChange={(e) => handleImageUpload && handleImageUpload(e.target.files)} accept="image/*" multiple className="hidden" />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* MENU IMAGES (Food Menu + Drinks Menu) */}
          <div className="space-y-6 border-t pt-6">
            <h3 className="text-base font-extrabold text-primary flex items-center gap-2 uppercase tracking-tight">
              <FileText className="w-5 h-5 text-primary" /> Menu Card Images
            </h3>

            {/* Food Menu Images */}
            <div className="bg-slate-50/80 p-5 rounded-2xl border border-slate-200/60">
              <label className="block text-xs font-black text-gray-700 uppercase tracking-wider mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4 text-emerald-600" />
                Food Menu Images ({currentData.menu_image_url?.length || 0})
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {currentData.menu_image_url?.map((image, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={image}
                      alt={`Food Menu ${index+1}`}
                      className="w-full h-28 object-cover rounded-xl cursor-pointer border border-gray-200 shadow-sm"
                      onClick={() => setGalleryConfig && setGalleryConfig({ isOpen: true, images: currentData.menu_image_url, index })}
                    />
                    <button
                      type="button"
                      onClick={() => removeMenuImage && removeMenuImage(index)}
                      className="absolute top-1.5 right-1.5 bg-rose-500 text-white rounded-full p-1.5 shadow-md hover:scale-110 transition-transform"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                <div 
                  className="border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:bg-white h-28 bg-white/50 transition-all group"
                  onClick={() => menuFileInputRef?.current?.click()}
                >
                  <FileText className="w-6 h-6 text-gray-400 mb-1 group-hover:scale-110 transition-transform" />
                  <span className="text-xs font-bold text-gray-600">Add Food Menu</span>
                  <input type="file" ref={menuFileInputRef} onChange={(e) => handleMenuImageUpload && handleMenuImageUpload(e.target.files)} accept="image/*" multiple className="hidden" />
                </div>
              </div>
            </div>

            {/* Drinks Menu Images */}
            <div className="bg-slate-50/80 p-5 rounded-2xl border border-slate-200/60">
              <label className="block text-xs font-black text-gray-700 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Coffee className="w-4 h-4 text-sky-600" />
                Drinks Menu Images ({currentData.beverage_image_url?.length || 0})
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {currentData.beverage_image_url?.map((image, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={image}
                      alt={`Drinks Menu ${index+1}`}
                      className="w-full h-28 object-cover rounded-xl cursor-pointer border border-gray-200 shadow-sm"
                      onClick={() => setGalleryConfig && setGalleryConfig({ isOpen: true, images: currentData.beverage_image_url, index })}
                    />
                    <button
                      type="button"
                      onClick={() => removeBeverageImage && removeBeverageImage(index)}
                      className="absolute top-1.5 right-1.5 bg-rose-500 text-white rounded-full p-1.5 shadow-md hover:scale-110 transition-transform"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                <div 
                  className="border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:bg-white h-28 bg-white/50 transition-all group"
                  onClick={() => beverageFileInputRef?.current?.click()}
                >
                  <Coffee className="w-6 h-6 text-gray-400 mb-1 group-hover:scale-110 transition-transform" />
                  <span className="text-xs font-bold text-gray-600">Add Drinks Menu</span>
                  <input type="file" ref={beverageFileInputRef} onChange={(e) => handleBeverageImageUpload && handleBeverageImageUpload(e.target.files)} accept="image/*" multiple className="hidden" />
                </div>
              </div>
            </div>
          </div>

          {/* SAVE BUTTON AT BOTTOM */}
          <div className="border-t pt-6 flex justify-end">
            <button
              type="button"
              onClick={handleSaveChanges}
              disabled={isLoading}
              className="flex items-center gap-2 px-8 py-3.5 bg-primary hover:bg-primary/90 text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-lg shadow-primary/20 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
            >
              <Save size={18} />
              <span>{isLoading ? 'Saving Changes...' : 'Save Gallery Images'}</span>
            </button>
          </div>
        </div>
      )}

      {/* TAB 3: LOCATION & OPERATING HOURS */}
      {activeTab === 'location' && (
        <div key="location" className="bg-white p-6 sm:p-8 rounded-3xl border border-gray-100 shadow-sm space-y-8 animate-tab-switch">
          {/* ADDRESS & LOCATION DETAILS */}
          <div className="space-y-4">
            <h3 className="text-base font-extrabold text-gray-800 uppercase tracking-tight flex items-center gap-2">
              <MapPin className="w-5 h-5 text-primary" /> Address & Location Details
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">City</label>
                <input 
                  type="text" 
                  name="city" 
                  defaultValue={currentData.city || ''} 
                  onChange={handleInputChange} 
                  className="w-full p-3 border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all" 
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Pincode</label>
                <input 
                  type="text" 
                  name="pincode" 
                  defaultValue={currentData.pincode || ''} 
                  onChange={handleInputChange} 
                  className="w-full p-3 border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all" 
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Full Address</label>
              <textarea 
                name="address" 
                defaultValue={currentData.address || ''} 
                onChange={handleInputChange} 
                rows={2} 
                className="w-full p-3 border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all" 
              />
            </div>

            {/* GOOGLE MAPS LINK & COORDINATES EXTRACTION */}
            <div className="bg-slate-50/90 p-6 rounded-3xl border border-slate-200/80 space-y-5">
              {/* MAPS LINK INPUT */}
              <div>
                <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">
                  MAPS LINK
                </label>
                <div className="relative flex items-center">
                  <input
                    type="text"
                    name="map_url"
                    value={mapUrlInput}
                    onChange={(e) => {
                      setMapUrlInput(e.target.value);
                      handleInputChange(e);
                    }}
                    placeholder="Paste Google Maps URL"
                    className="w-full pl-5 pr-28 py-3.5 bg-white border border-gray-200/90 rounded-2xl text-sm font-medium text-gray-800 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-gray-400 shadow-sm"
                  />
                  <button
                    type="button"
                    onClick={() => handleExtractCoordinates(mapUrlInput)}
                    className="absolute right-2 bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 text-xs font-black uppercase tracking-wider px-4 py-2 rounded-xl transition-all shadow-sm active:scale-95 border border-blue-100"
                  >
                    Extract
                  </button>
                </div>
              </div>

              {/* LATITUDE & LONGITUDE INPUTS */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">
                    LATITUDE
                  </label>
                  <input
                    type="number"
                    step="any"
                    name="latitude"
                    value={latitudeInput}
                    onChange={(e) => {
                      const val = e.target.value;
                      setLatitudeInput(val);
                      const refToUpdate = formDataRef?.current ? formDataRef.current : localFormDataRef.current;
                      refToUpdate.latitude = val ? parseFloat(val) : '';
                      if (formDataRef) formDataRef.current.latitude = val ? parseFloat(val) : '';
                    }}
                    placeholder="15.XXXX"
                    className="w-full p-3.5 bg-white border border-gray-200/90 rounded-2xl text-sm font-medium text-gray-800 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-gray-400 shadow-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">
                    LONGITUDE
                  </label>
                  <input
                    type="number"
                    step="any"
                    name="longitude"
                    value={longitudeInput}
                    onChange={(e) => {
                      const val = e.target.value;
                      setLongitudeInput(val);
                      const refToUpdate = formDataRef?.current ? formDataRef.current : localFormDataRef.current;
                      refToUpdate.longitude = val ? parseFloat(val) : '';
                      if (formDataRef) formDataRef.current.longitude = val ? parseFloat(val) : '';
                    }}
                    placeholder="73.XXXX"
                    className="w-full p-3.5 bg-white border border-gray-200/90 rounded-2xl text-sm font-medium text-gray-800 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-gray-400 shadow-sm"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* OPERATING HOURS & DAYS */}
          <div className="border-t pt-6 space-y-4">
            <h3 className="text-base font-extrabold text-gray-800 flex items-center gap-2 uppercase tracking-tight">
              <Clock className="w-5 h-5 text-primary" /> Operating Hours & Days
            </h3>

            {/* Operating Days */}
            <div className="bg-slate-50/80 p-5 rounded-2xl border border-slate-200/60">
              <label className="block text-xs font-black text-gray-700 uppercase tracking-wider mb-2">Operating Days</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
                {daysOfWeek.map((day) => (
                  <label key={day} className="flex items-center text-xs font-medium text-gray-700 cursor-pointer bg-white px-3 py-2 rounded-xl border border-gray-200/80 shadow-sm hover:border-primary/40 transition-all">
                    <input
                      type="checkbox"
                      name="days"
                      value={day}
                      checked={currentData.businessHours?.days?.includes(day) || false}
                      onChange={handleDaysChange}
                      className="mr-2 rounded text-primary focus:ring-primary"
                    />
                    {day.slice(0, 3)}
                  </label>
                ))}
              </div>
            </div>

            {/* Slot 1 (Main Operating Hours) */}
            <div className="bg-slate-50/80 p-5 rounded-2xl border border-slate-200/60">
              <label className="block text-xs font-black text-gray-700 uppercase tracking-wider mb-3">
                Slot 1 / Primary Operating Hours (24-Hour Format)
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1">Opening Time (Slot 1)</label>
                  <input
                    type="time"
                    name="businessHours.openingTime"
                    value={businessHoursState.openingTime || ''}
                    onChange={handleInputChange}
                    className="w-full p-3 bg-white border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1">Closing Time (Slot 1)</label>
                  <input
                    type="time"
                    name="businessHours.closingTime"
                    value={businessHoursState.closingTime || ''}
                    onChange={handleInputChange}
                    className="w-full p-3 bg-white border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  />
                </div>
              </div>
            </div>

            {/* Slot 2 (Split Hours / Evening Slot) */}
            <div className="bg-slate-50/80 p-5 rounded-2xl border border-slate-200/60 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-xs font-black text-gray-700 uppercase tracking-wider block">
                    Slot 2 / Split Shift (e.g., 12:30 - 15:30 & 18:30 - 22:30)
                  </label>
                  <p className="text-[11px] text-gray-500 font-medium">Enable if your restaurant closes in the afternoon and reopens for evening service</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hasSecondShift}
                    onChange={(e) => {
                      const enabled = e.target.checked;
                      setHasSecondShift(enabled);
                      if (!enabled) {
                        const refToUpdate = formDataRef?.current ? formDataRef.current : localFormDataRef.current;
                        if (refToUpdate.businessHours) {
                          refToUpdate.businessHours.openingTime2 = '';
                          refToUpdate.businessHours.closingTime2 = '';
                        }
                        setBusinessHoursState(prev => ({
                          ...prev,
                          openingTime2: '',
                          closingTime2: ''
                        }));
                      }
                    }}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>

              {hasSecondShift && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-3 border-t border-gray-200/60 animate-in fade-in duration-200">
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1">Opening Time (Slot 2)</label>
                    <input
                      type="time"
                      name="businessHours.openingTime2"
                      value={businessHoursState.openingTime2 || ''}
                      onChange={handleInputChange}
                      className="w-full p-3 bg-white border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1">Closing Time (Slot 2)</label>
                    <input
                      type="time"
                      name="businessHours.closingTime2"
                      value={businessHoursState.closingTime2 || ''}
                      onChange={handleInputChange}
                      className="w-full p-3 bg-white border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* SAVE BUTTON AT BOTTOM */}
          <div className="border-t pt-6 flex justify-end">
            <button
              type="button"
              onClick={handleSaveChanges}
              disabled={isLoading}
              className="flex items-center gap-2 px-8 py-3.5 bg-primary hover:bg-primary/90 text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-lg shadow-primary/20 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
            >
              <Save size={18} />
              <span>{isLoading ? 'Saving Changes...' : 'Save Location & Hours'}</span>
            </button>
          </div>
        </div>
      )}

      {/* TAB 4: RESTAURANT TAGS */}
      {activeTab === 'tags' && (
        <div key="tags" className="bg-white p-6 sm:p-8 rounded-3xl border border-gray-100 shadow-sm space-y-8 animate-tab-switch">
          {/* HEADER & SEARCH */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-100 pb-6">
            <div>
              <div className="flex items-center gap-3">
                <h3 className="text-base font-extrabold text-gray-900 uppercase tracking-tight flex items-center gap-2">
                  <TagIcon className="w-5 h-5 text-primary" /> Restaurant Tags
                </h3>
                <span className="text-[11px] font-bold text-primary bg-primary/10 px-3 py-1 rounded-full border border-primary/20">
                  {selectedSecondaryTags.length} Selected
                </span>
              </div>
              <p className="text-xs text-gray-500 font-medium mt-1">
                Select tags for your restaurant features, amenities, and ambience.
              </p>
            </div>

            {/* SEARCH INPUT */}
            <div className="relative w-full md:w-72">
              <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={tagSearchQuery}
                onChange={(e) => setTagSearchQuery(e.target.value)}
                placeholder="Search tags..."
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-gray-400"
              />
            </div>
          </div>

          {isTagsLoading ? (
            <div className="py-12 flex flex-col items-center justify-center space-y-3 text-gray-400">
              <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin"></div>
              <p className="text-xs font-bold uppercase tracking-wider">Loading restaurant tags...</p>
            </div>
          ) : tagsList.length === 0 ? (
            <div className="py-12 text-center text-gray-400 bg-slate-50 rounded-2xl border border-dashed border-gray-200">
              <TagIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-xs font-bold">No restaurant tags found.</p>
            </div>
          ) : (
            <div>
              {(() => {
                const filteredTags = tagsList.filter(t => t.name.toLowerCase().includes(tagSearchQuery.toLowerCase()));
                if (filteredTags.length === 0) {
                  return <p className="text-xs text-gray-400 italic py-4">No tags match your search query.</p>;
                }
                return (
                  <div className="flex flex-wrap gap-2.5">
                    {filteredTags.map((tag) => {
                      const isSelected = selectedSecondaryTags.includes(tag._id);
                      return (
                        <button
                          key={tag._id}
                          type="button"
                          onClick={() => toggleSecondaryTag(tag._id)}
                          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all shadow-sm active:scale-95 border ${
                            isSelected
                              ? 'bg-primary text-white border-primary shadow-primary/20 scale-[1.02]'
                              : 'bg-slate-50 text-slate-700 hover:bg-slate-100/90 border-slate-200/80'
                          }`}
                        >
                          {isSelected ? <Check className="w-3.5 h-3.5 text-white" /> : <TagIcon className="w-3.5 h-3.5 text-gray-400" />}
                          <span>{tag.name}</span>
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}

          {/* SAVE BUTTON AT BOTTOM */}
          <div className="border-t pt-6 flex justify-end">
            <button
              type="button"
              onClick={handleSaveChanges}
              disabled={isLoading}
              className="flex items-center gap-2 px-8 py-3.5 bg-primary hover:bg-primary/90 text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-lg shadow-primary/20 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
            >
              <Save size={18} />
              <span>{isLoading ? 'Saving Changes...' : 'Save Restaurant Tags'}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RestaurantProfile;
